import type { FC } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useInvestin } from "../hooks/managedFunds/useInvestin";
import type { ManagedFund, ManagedFundInvestment } from "../types/managedFunds";

type ManagedFundsContextState = {
  managedFunds: ManagedFund;
  managedFundsBalance: number;
  setManagedFundsBalance: (balance: number) => void;
};

const ManagedFundsContext = createContext<ManagedFundsContextState | null>(null);

export const ManagedFundsContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const investinPositions = useInvestin();
  const [managedFundsBalance, setManagedFundsBalance] = useState(0);

  const investmentData = useMemo(() => {
    const fundPositions: ManagedFund[] = [...investinPositions];
    let investmentTotal = 0;
    const investments: ManagedFundInvestment[] = [];
    for (const fund of fundPositions) {
      investmentTotal += fund.sumReturns;
      Array.prototype.push.apply(investments, fund.investments);
    }
    return { sumReturns: investmentTotal, investments };
  }, [investinPositions]);

  useEffect(() => {
    setManagedFundsBalance(investmentData.sumReturns);
  }, [investmentData.sumReturns]);

  return (
    <ManagedFundsContext.Provider
      value={{
        managedFunds: investmentData,
        managedFundsBalance,
        setManagedFundsBalance,
      }}
    >
      {children}
    </ManagedFundsContext.Provider>
  );
};

export function useManagedFundsContext(): ManagedFundsContextState {
  const ctx = useContext(ManagedFundsContext);
  if (ctx === null) {
    throw new Error("Context not available");
  }
  return ctx;
}
